import React, { useEffect, useRef, useState } from 'react';

import { Alert } from '@mantine/core';
import { IconArrowBackUp } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import { useTracking } from 'hooks/mixpanel/useTracking';

import { useCheckout } from '../../CheckoutContext';

// The dismissible welcome-back banner (spec Item 2): shown when saved checkout
// progress was restored and the buyer holds an active session — whether the buyer
// came back in the same session or through the recovery email's link (spec Item 11).
// Dismissal is per visit — the banner may reappear on a later return.
const ResumeBanner = (): JSX.Element | null => {
  const { wasResumedFromStorage, resumeSource, packageData } = useCheckout();
  const { session } = useAuth();
  const { trackSalesPortalCheckoutResumed } = useTracking();
  const [dismissed, setDismissed] = useState<boolean>(false);
  const hasTrackedRef = useRef<boolean>(false);

  const visible = wasResumedFromStorage && !!session?.user && !dismissed;

  useEffect(() => {
    if (!visible || hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    trackSalesPortalCheckoutResumed(packageData.gym.gymId, packageData.packageId, resumeSource);
  }, [visible, trackSalesPortalCheckoutResumed, packageData.gym.gymId, packageData.packageId, resumeSource]);

  if (!visible) return null;

  return (
    <Alert
      icon={<IconArrowBackUp size='1rem' />}
      color='blue'
      withCloseButton
      closeButtonLabel='Dismiss'
      onClose={() => setDismissed(true)}
      data-cy='checkoutResumeBanner'
      data-resume-source={resumeSource}
    >
      {resumeSource === 'email'
        ? "Welcome back! You're signed in and your progress is saved. Pick up right where you left off — your details below are already filled in."
        : 'Welcome back! We saved your progress from your last visit. Pick up right where you left off — your details below are already filled in.'}
    </Alert>
  );
};

export default ResumeBanner;
